export const artistTerms = {
    title: "Terms and Conditions for Artists & Shops",
    lastUpdated: "December 17, 2025",
    effectiveDate: "December 17, 2025",
    
    introduction: `These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("User," "Artist," "Shop," or "you") and Inked Up Manager ("we," "us," or "our") governing your access to and use of the Inked Up Manager software platform and related services (collectively, the "Service").`,
    
    importantNotice: `BY CREATING AN ACCOUNT, SUBSCRIBING TO, OR USING OUR SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS.`,
    
    sections: [
      {
        id: "eligibility-authorization",
        title: "ELIGIBILITY AND AUTHORIZATION",
        subsections: [
          {
            id: "age-legal-capacity",
            title: "Age and Legal Capacity",
            content: "You must be at least 18 years of age and have the legal capacity to enter into binding contracts in your jurisdiction."
          },
          {
            id: "business-authorization",
            title: "Business Authorization",
            content: "If registering on behalf of a business entity, you represent and warrant that:",
            list: [
              "You are authorized to bind that entity to these Terms",
              "You have the legal authority to accept these Terms on behalf of the business",
              "The business is validly existing and in good standing"
            ]
          },
          {
            id: "professional-requirements",
            title: "Professional Requirements",
            content: "You represent that you:",
            list: [
              "Hold all necessary licenses, permits, and certifications required in your jurisdiction",
              "Comply with all applicable health department regulations and safety standards",
              "Maintain current professional liability insurance as required by law"
            ]
          }
        ]
      },
  
      {
        id: "account-registration",
        title: "ACCOUNT REGISTRATION AND SECURITY",
        subsections: [
          {
            id: "account-information",
            title: "Account Information",
            content: "You agree to:",
            list: [
              "Provide accurate, complete, and current information during registration",
              "Promptly update account information to maintain accuracy",
              "Use your real name and business information (no pseudonyms or false identities)"
            ]
          },
          {
            id: "account-security",
            title: "Account Security",
            content: "You are solely responsible for:",
            list: [
              "Maintaining the confidentiality of your login credentials",
              "All activities that occur under your account",
              "Immediately notifying us of any unauthorized access or security breaches",
              "Using strong passwords and enabling two-factor authentication when available"
            ]
          },
          {
            id: "account-restrictions",
            title: "Account Restrictions",
            content: "Each user may maintain only one active account. Multiple accounts may result in suspension or termination."
          }
        ]
      },
  
      {
        id: "subscription-billing",
        title: "SUBSCRIPTION PLANS AND BILLING",
        subsections: [
          {
            id: "subscription-tiers",
            title: "Subscription Tiers",
            list: [
              "Basic Plan: $35/month - Core client management features",
              "Standard Plan: $85/month - Enhanced features including email automation",
              "Starter Studio Plan: $199/month - Full feature suite with advanced analytics",
              "Growth Studio Plan: $299/month - All features plus priority support"
            ]
          },
          {
            id: "feature-variability",
            title: "Feature Variability by Tier",
            content: "Features, data presentation, record organization, and reporting capabilities may vary by subscription tier. Record compilation, summaries, or generated reports are provided for convenience only. No tier guarantees legal sufficiency, regulatory compliance, or completeness of records."
          },
          {
            id: "billing-terms",
            title: "Billing Terms",
            list: [
              "All subscriptions are billed monthly in advance",
              "Payments are processed automatically on your billing date",
              "You authorize us to charge your designated payment method",
              "Failed payments may result in service suspension after reasonable notice"
            ]
          },
          {
            id: "price-changes",
            title: "Price Changes",
            content: "We reserve the right to modify subscription prices with 30 days' written notice. Continued use of the Service after price changes constitutes acceptance of new pricing."
          },
          {
            id: "refund-policy",
            title: "Refund Policy",
            content: "All fees are non-refundable except:",
            list: [
              "Technical service failures lasting more than 48 consecutive hours",
              "Billing errors on our part",
              "Cancellation within 7 days of initial subscription (one-time only)"
            ]
          },
          {
            id: "taxes",
            title: "Taxes",
            content: "You are responsible for all applicable taxes, duties, and fees related to your use of the Service."
          }
        ]
      },
  
      {
        id: "acceptable-use",
        title: "ACCEPTABLE USE POLICY",
        subsections: [
          {
            id: "permitted-uses",
            title: "Permitted Uses",
            content: "You may use the Service only for legitimate business purposes related to tattoo shop management and client services."
          },
          {
            id: "prohibited-activities",
            title: "Prohibited Activities",
            content: "You agree NOT to:",
            list: [
              "Store, transmit, or distribute illegal, harmful, or offensive content",
              "Violate any applicable laws, regulations, or third-party rights",
              "Attempt to reverse engineer, decompile, or extract source code",
              "Use automated systems to access the Service without permission",
              "Resell, sublicense, or transfer your account to others",
              "Impersonate others or provide false information",
              "Interfere with or disrupt the Service's functionality",
              "Use the Service to compete directly with our business"
            ]
          },
          {
            id: "content-standards",
            title: "Content Standards",
            content: "All content stored on the platform must comply with:",
            list: [
              "Professional standards for healthcare documentation",
              "Privacy laws and regulations",
              "Our community guidelines and professional standards"
            ]
          }
        ]
      },
  
      {
        id: "regulatory-compliance",
        title: "REGULATORY COMPLIANCE AND CLIENT DATA",
        subsections: [
          {
            id: "compliance-obligations",
            title: "Your Compliance Obligations",
            content: "You are solely responsible for:",
            list: [
              "Complying with all federal, state, and local health regulations",
              "Meeting consent form requirements in your jurisdiction",
              "Obtaining proper client authorizations for data collection and storage",
              "Maintaining required business licenses and permits",
              "Following all applicable privacy laws (HIPAA, state privacy laws, etc.)"
            ]
          },
          {
            id: "health-department-compliance",
            title: "Health Department Compliance",
            content: "You acknowledge that:",
            list: [
              "Requirements vary by jurisdiction and may change",
              "You must independently verify and maintain compliance",
              "Inked Up Manager does not provide legal or regulatory advice",
              "Our tools are designed to assist but do not guarantee compliance"
            ]
          },
          {
            id: "data-processing",
            title: "Data Processing",
            list: [
              "You grant us permission to process client data solely to provide the Service",
              "We implement appropriate security measures but cannot guarantee absolute security",
              "You remain the data controller for all client information",
              "You must have legal basis for all data processing activities"
            ]
          },
          {
            id: "record-storage-tools",
            title: "Record Storage and Access",
            content: "The Service provides record storage and access tools. Certain subscription tiers may offer convenience features for compiling or organizing records; however, all users remain responsible for determining what records are required and how they are used."
          },
          {
            id: "record-completeness",
            title: "Record Completeness",
            content: "Records stored within the Service are based on user input and actions. Inked Up Manager does not verify completeness, accuracy, or timeliness of records and is not responsible for missing or incomplete documentation."
          },
          {
            id: "record-assembly-disclaimer",
            title: "Record Assembly Disclaimer",
            content: "Any compiled records, summaries, or generated documents are provided for organizational convenience only and do not constitute legal opinions, legal documents, or compliance certifications."
          },
          {
            id: "third-party-communications",
            title: "Third-Party Communications",
            content: "Communications sent through third-party providers (including email notifications, reminders, and aftercare instructions) are subject to delivery limitations outside our control. Inked Up Manager does not guarantee receipt, delivery, or timely arrival of such communications."
          },
          {
            id: "system-of-record",
            title: "System of Record",
            content: "The Service is not intended to serve as a user's sole system of record. Users are responsible for maintaining independent backups or copies of records as required by their business or legal obligations."
          },
          {
            id: "user-decision-responsibility",
            title: "User Decision Responsibility",
            content: "All business decisions, including whether to proceed with appointments, services, or documentation, remain solely the responsibility of the user. The Service does not make decisions on behalf of users or validate the appropriateness of any user action."
          },
          {
            id: "client-data-accuracy",
            title: "Client Data Accuracy",
            content: "The Service does not verify the accuracy of client-provided information, including but not limited to age, identity, medical conditions, or consent capacity. Users are solely responsible for verifying client information as required by applicable laws and professional standards."
          },
          {
            id: "studio-member-responsibility",
            title: "Studio Member Responsibility",
            content: "For studio subscription plans, the account owner is responsible for the actions of all authorized studio members using the Service. The account owner must ensure all members comply with these Terms and applicable laws."
          }
        ]
      },
  
      {
        id: "intellectual-property",
        title: "INTELLECTUAL PROPERTY",
        subsections: [
          {
            id: "our-rights",
            title: "Our Rights",
            content: "Inked Up Manager retains all rights, title, and interest in:",
            list: [
              "The software platform and its features",
              "Our trademarks, logos, and branding",
              "All improvements and updates to the Service",
              "Platform-generated analytics and aggregated usage data"
            ]
          },
          {
            id: "your-rights",
            title: "Your Rights",
            content: "You retain ownership of:",
            list: [
              "Your client data and business information",
              "Content you create using our tools",
              "Your existing trademarks and intellectual property"
            ]
          },
          {
            id: "license-grant",
            title: "License Grant",
            content: "We grant you a limited, non-exclusive, non-transferable license to use the Service during your subscription term, subject to these Terms."
          }
        ]
      },
  
      {
        id: "service-availability",
        title: "SERVICE AVAILABILITY AND SUPPORT",
        subsections: [
          {
            id: "uptime-commitment",
            title: "Uptime Commitment",
            content: "We strive to maintain 99.5% uptime but do not guarantee uninterrupted service availability."
          },
          {
            id: "scheduled-maintenance",
            title: "Scheduled Maintenance",
            content: "We may perform scheduled maintenance with reasonable advance notice, typically during off-peak hours."
          },
          {
            id: "support-services",
            title: "Support Services",
            list: [
              "Standard support is provided via email and help documentation",
              "Premium support options may be available for higher-tier plans",
              "Support response times vary by subscription level"
            ]
          },
          {
            id: "service-interruptions",
            title: "Service Interruptions",
            content: "Temporary unavailability of records due to maintenance, outages, or external factors does not relieve users of their obligation to maintain required documentation. Users should maintain independent access to critical records as part of their business continuity planning."
          },
          {
            id: "third-party-integrations",
            title: "Third-Party Integrations",
            content: "The Service may integrate with third-party services (including payment processors, calendar systems, and communication providers). We are not responsible for the availability, accuracy, or functionality of third-party services, and their use is subject to their respective terms and conditions."
          }
        ]
      },
  
      {
        id: "privacy-data-protection",
        title: "PRIVACY AND DATA PROTECTION",
        subsections: [
          {
            id: "privacy-policy-ref",
            title: "Privacy Policy",
            content: "Our Privacy Policy is incorporated by reference and governs our collection and use of your information."
          },
          {
            id: "data-security",
            title: "Data Security",
            content: "We implement industry-standard security measures including:",
            list: [
              "Encryption of data in transit and at rest",
              "Regular security audits and updates",
              "Access controls and authentication systems",
              "Secure backup and recovery procedures"
            ]
          },
          {
            id: "data-retention",
            title: "Data Retention",
            list: [
              "Active account data is retained during your subscription",
              "Deleted data is permanently removed within 30 days unless legally required to retain",
              "Backup data may be retained for up to 90 days for disaster recovery"
            ]
          },
          {
            id: "user-export-responsibility",
            title: "User Export Responsibility",
            content: "Users are responsible for exporting and retaining copies of their records as needed to meet legal or business requirements. Inked Up Manager is not responsible for records not exported prior to account termination or data deletion."
          },
          {
            id: "data-export-format",
            title: "Data Export Format",
            content: "Exported data is provided in standard formats (such as PDF or CSV) as available at the time of export. We do not guarantee compatibility with specific third-party systems or future format availability."
          }
        ]
      },
  
      {
        id: "limitation-liability",
        title: "LIMITATION OF LIABILITY",
        subsections: [
          {
            id: "disclaimer-warranties",
            title: "Disclaimer of Warranties",
            content: "THE SERVICE IS PROVIDED \"AS IS\" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT."
          },
          {
            id: "limitation-damages",
            title: "Limitation of Damages",
            content: "TO THE MAXIMUM EXTENT PERMITTED BY LAW, INKED UP MANAGER SHALL NOT BE LIABLE FOR:",
            list: [
              "Indirect, incidental, special, consequential, or punitive damages",
              "Lost profits, revenue, data, or business opportunities",
              "Service interruptions or data loss",
              "Third-party claims or actions",
              "Your failure to comply with applicable laws or regulations"
            ]
          },
          {
            id: "maximum-liability",
            title: "Maximum Liability",
            content: "Our total liability to you for any claim shall not exceed the amount paid by you in the 12 months preceding the claim."
          }
        ]
      },
  
      {
        id: "indemnification",
        title: "INDEMNIFICATION",
        content: "You agree to indemnify, defend, and hold harmless Inked Up Manager from any claims, damages, losses, or expenses (including attorney fees) arising from:",
        list: [
          "Your use of the Service",
          "Your violation of these Terms",
          "Your violation of any law or regulation",
          "Your professional services provided to clients",
          "Any content you submit or store on the platform"
        ]
      },
  
      {
        id: "termination",
        title: "TERMINATION",
        subsections: [
          {
            id: "termination-by-you",
            title: "Termination by You",
            content: "You may cancel your subscription at any time through your account settings or by contacting support."
          },
          {
            id: "termination-by-us",
            title: "Termination by Us",
            content: "We may suspend or terminate your account for:",
            list: [
              "Violation of these Terms",
              "Non-payment of fees",
              "Illegal or harmful activities",
              "Extended periods of inactivity"
            ]
          },
          {
            id: "effect-termination",
            title: "Effect of Termination",
            content: "Upon termination:",
            list: [
              "Your access to the Service will cease",
              "You may download your data for 30 days",
              "We will delete your data according to our retention policy",
              "Outstanding fees remain due and payable"
            ]
          }
        ]
      },
  
      {
        id: "updates-modifications",
        title: "UPDATES AND MODIFICATIONS",
        subsections: [
          {
            id: "service-updates",
            title: "Service Updates",
            content: "We regularly update and improve the Service. Some updates may change functionality or require additional agreements."
          },
          {
            id: "feature-changes",
            title: "Feature Changes",
            content: "Feature behavior, availability, and presentation may evolve over time, provided that core record access remains available during an active subscription. Users should not rely on any single feature or workflow as the exclusive means of fulfilling legal, regulatory, or operational obligations."
          },
          {
            id: "beta-preview-features",
            title: "Beta and Preview Features",
            content: "Certain features may be designated as beta, preview, or experimental. Such features are provided without warranty and may be modified, suspended, or discontinued at any time without notice. Users should not rely on beta features for critical business operations."
          },
          {
            id: "terms-modifications",
            title: "Terms Modifications",
            content: "We may modify these Terms with 30 days' notice for material changes. Continued use constitutes acceptance of modified Terms."
          }
        ]
      },
  
      {
        id: "dispute-resolution",
        title: "DISPUTE RESOLUTION",
        subsections: [
          {
            id: "governing-law",
            title: "Governing Law",
            content: "These Terms are governed by the laws of the State of Georgia, without regard to conflict of law principles."
          },
          {
            id: "arbitration",
            title: "Arbitration",
            content: "Any dispute, claim, or controversy arising out of or relating to these Terms or the Service shall be resolved exclusively through binding arbitration in Gwinnett County, Georgia under the rules of the American Arbitration Association. You agree that any arbitration shall be conducted on an individual basis and not as a class, consolidated, or representative action."
          },
          {
            id: "class-action-waiver",
            title: "Class Action Waiver",
            content: "You waive the right to participate in class action lawsuits or class-wide arbitration."
          }
        ]
      },
  
      {
        id: "general-provisions",
        title: "GENERAL PROVISIONS",
        subsections: [
          {
            id: "entire-agreement",
            title: "Entire Agreement",
            content: "These Terms, together with our Privacy Policy, constitute the entire agreement between you and Inked Up Manager."
          },
          {
            id: "severability",
            title: "Severability",
            content: "If any provision is found invalid or unenforceable, the remaining provisions shall remain in full force and effect."
          },
          {
            id: "assignment",
            title: "Assignment",
            content: "You may not assign your rights under these Terms without our written consent. We may assign our rights and obligations without restriction."
          },
          {
            id: "force-majeure",
            title: "Force Majeure",
            content: "Neither party shall be liable for delays or failures due to circumstances beyond their reasonable control."
          },
          {
            id: "waiver",
            title: "Waiver",
            content: "Our failure to enforce any provision of these Terms shall not constitute a waiver of that provision or any other provision. Any waiver must be in writing and signed by an authorized representative."
          },
          {
            id: "no-third-party-beneficiaries",
            title: "No Third-Party Beneficiaries",
            content: "These Terms are between you and Inked Up Manager. No third party, including your clients, employees, or contractors, shall have any rights under these Terms."
          },
          {
            id: "no-fiduciary-relationship",
            title: "No Fiduciary Relationship",
            content: "Nothing in these Terms creates any fiduciary, partnership, employment, or agency relationship between you and Inked Up Manager."
          },
          {
            id: "no-professional-advice",
            title: "No Professional Advice",
            content: "The Service does not provide medical, health, legal, financial, or professional advice of any kind. Any information provided through the Service is for general informational purposes only and should not be relied upon as a substitute for professional consultation."
          },
          {
            id: "survival",
            title: "Survival",
            content: "Sections relating to limitation of liability, indemnification, dispute resolution, intellectual property, and user responsibility shall survive termination of these Terms."
          }
        ]
      },
  
      {
        id: "contact-information",
        title: "CONTACT INFORMATION",
        content: [
          "For questions about these Terms or the Service:",
          "Email: legal@inkedupmanager.com",
          "Address: 1250 Scenic Highway S STE 1701 #206, Lawrenceville, GA 30045",
          //"Phone: [Company Phone]",
          "Business Hours: Monday-Friday, 9 AM - 5 PM EST",
          "",
          "By creating an account or using the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions."
        ]
      }
    ]
  };